import { http } from './apiFetcher';

/**
 * Subscribe to a `text/event-stream` response on a path served by our Api.
 *
 * The browser's <c>EventSource</c> can't pass an <c>Authorization: Bearer</c>
 * header, and our admin routes reject unauth calls. So we use <c>fetch</c>
 * directly (which the shared axios interceptor stays out of), manually parse
 * <c>data: &lt;line&gt;\n\n</c> frames out of a streamed <c>ReadableStream</c>,
 * and invoke <c>onEvent</c> on every complete frame.
 *
 * Returns an `unsubscribe` function that aborts the fetch and closes the stream.
 */
export interface SseStreamOptions<T> {
  path: string;
  onEvent: (event: T) => void;
  onError?: (err: unknown) => void;
  accessToken?: string | null;
}

export function streamSse<T = unknown>({
  path,
  onEvent,
  onError,
  accessToken,
}: SseStreamOptions<T>): () => void {
  const controller = new AbortController();
  const headers: Record<string, string> = { Accept: 'text/event-stream' };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  // Use the axios baseURL so dev + prod both work against the Vite proxy.
  const baseUrl = typeof http.defaults.baseURL === 'string' ? http.defaults.baseURL : '';
  const url = baseUrl.replace(/\/$/, '') + path;

  (async () => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers,
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`SSE subscribe failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (!controller.signal.aborted) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Split on blank lines — SSE framing.
        let sep = buffer.indexOf('\n\n');
        while (sep !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);

          for (const line of frame.split('\n')) {
            if (!line.startsWith('data:')) continue;
            const payload = line.slice(5).trimStart();
            if (!payload) continue;
            try {
              onEvent(JSON.parse(payload) as T);
            } catch {
              onEvent(payload as unknown as T);
            }
          }
          sep = buffer.indexOf('\n\n');
        }
      }
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        onError?.(err);
      }
    }
  })();

  return () => controller.abort();
}
